/**
 * SimpleNewsListing - News listing view
 *
 * Displays two sections of news_item_ess content:
 * 1. Internal news (media_news = false) — 6 items per page, sorted by effective desc.
 * 2. ESS in the Media (media_news = true) — 4 items per page, sorted by effective desc.
 *
 * Header: PageHeaderBackground
 * Pagination: design system Pagination molecule (server-side)
 * Error handling: per-section error messages
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { PageHeaderBackground } from '../../Molecules/PageHeaderBackground';
import {
  ContentWide,
  ContentWrapper,
  PageSection,
} from 'volto-essvoltocomponents/components/Layout';
import ResponsiveGrid from '../../Atoms/ResponsiveGrid';
import Carousel from '../../Atoms/Carousel';
import ResponsiveComponentSwitch, {
  useWindowWidth,
} from '../../Atoms/ResponsiveComponentSwitch';
import TeaserCardVertical from '../../Molecules/TeaserCard/TeaserCardVertical';
import Pagination from '../../Molecules/Pagination/Pagination';
import {
  resolveImageURL,
  formatNewsDate,
} from '../../../helpers/multimediaUtils';

const INTERNAL_PAGE_SIZE = 6;
const EXTERNAL_PAGE_SIZE = 4;
const MOBILE_INTERNAL_PAGE_SIZE  = 6;
const MOBILE_EXTERNAL_PAGE_SIZE = 6;

const INTERNAL_GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 768, columns: 2 },
  { minWidth: 1024, columns: 3 },
  { minWidth: 1440, columns: 3 },
];

const EXTERNAL_GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 640, columns: 2 },
  { minWidth: 1024, columns: 4 },
];

const messages = defineMessages({
  externalNewsTitle: {
    id: 'news_listing.external_title',
    defaultMessage: 'External News',
  },
  noInternalNews: {
    id: 'news_listing.no_internal',
    defaultMessage: 'No news available at the moment.',
  },
  noExternalNews: {
    id: 'news_listing.no_external',
    defaultMessage: 'No external news available at the moment.',
  },
  errorInternal: {
    id: 'news_listing.error_internal',
    defaultMessage: 'Could not load news. Please try again later.',
  },
  errorExternal: {
    id: 'news_listing.error_external',
    defaultMessage: 'Could not load external news. Please try again later.',
  },
});

const SimpleNewsListing = ({ content }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [internalPage, setInternalPage] = useState(1);
  const [externalPage, setExternalPage] = useState(1);
  const internalSectionRef = useRef(null);
  const externalSectionRef = useRef(null);
  const [internalCarouselStartIndex, setInternalCarouselStartIndex] =
    useState(0);
  const [externalCarouselStartIndex, setExternalCarouselStartIndex] =
    useState(0);
  const windowWidth = useWindowWidth();
  const isCarouselLayout = windowWidth < 768;
  const internalItemsPerPage = isCarouselLayout ? MOBILE_INTERNAL_PAGE_SIZE : INTERNAL_PAGE_SIZE;
  const externalItemsPerPage = isCarouselLayout ? MOBILE_EXTERNAL_PAGE_SIZE : EXTERNAL_PAGE_SIZE;

  const scrollToRef = (ref) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  // ── Internal news subrequest ──────────────────────────────────────────────
const internalSubrequestKey = `newsInternal-p${internalPage}-s${internalItemsPerPage}`;
const internalItems = useSelector(
    (state) => state.search.subrequests?.[internalSubrequestKey]?.items || [],
  );
  const internalTotal = useSelector(
    (state) => state.search.subrequests?.[internalSubrequestKey]?.total || 0,
  );
  const internalLoading = useSelector(
    (state) =>
      state.search.subrequests?.[internalSubrequestKey]?.loading ?? false,
  );
  const internalLoaded = useSelector(
    (state) =>
      state.search.subrequests?.[internalSubrequestKey]?.loaded ?? false,
  );
  const internalError = useSelector(
    (state) => state.search.subrequests?.[internalSubrequestKey]?.error || null,
  );
  const internalTotalPages = Math.max(
    1,
    Math.ceil(internalTotal / internalItemsPerPage),
  );

  // ── External news subrequest ──────────────────────────────────────────────
  const externalSubrequestKey = `newsExternal-p${externalPage}-s${externalItemsPerPage}`;
  const externalItems = useSelector(
    (state) => state.search.subrequests?.[externalSubrequestKey]?.items || [],
  );
  const externalTotal = useSelector(
    (state) => state.search.subrequests?.[externalSubrequestKey]?.total || 0,
  );
  const externalLoading = useSelector(
    (state) =>
      state.search.subrequests?.[externalSubrequestKey]?.loading ?? false,
  );
  const externalLoaded = useSelector(
    (state) =>
      state.search.subrequests?.[externalSubrequestKey]?.loaded ?? false,
  );
  const externalError = useSelector(
    (state) => state.search.subrequests?.[externalSubrequestKey]?.error || null,
  );
  const externalTotalPages = Math.max(
    1,
    Math.ceil(externalTotal / externalItemsPerPage),
  );

  // ── Fetch internal news ───────────────────────────────────────────────────
  useEffect(() => {
    if (!internalLoaded) {
      dispatch(
        searchContent(
          '/news',
          {
            portal_type: ['news_item_ess'],
            media_news: false,
            review_state: 'published',
            sort_on: 'effective',
            sort_order: 'descending',
            b_size: internalItemsPerPage,
            b_start: (internalPage - 1) * internalItemsPerPage,
            metadata_fields: ['_all'],
          },
          internalSubrequestKey,
        ),
      );
    }
  }, [dispatch, internalPage, internalSubrequestKey, internalLoaded]);

  // ── Fetch external news ───────────────────────────────────────────────────
  useEffect(() => {
    if (!externalLoaded) {
      dispatch(
        searchContent(
          '/news',
          {
            portal_type: ['news_item_ess'],
            media_news: true,
            review_state: 'published',
            sort_on: 'effective',
            sort_order: 'descending',
            b_size: externalItemsPerPage,
            b_start: (externalPage - 1) * externalItemsPerPage,
            metadata_fields: ['image_scales', 'short_description'],
          },
          externalSubrequestKey,
        ),
      );
    }
  }, [dispatch, externalPage, externalSubrequestKey, externalLoaded]);

  // ── Hero image — uses hero_image from the multimedia content type schema ──
  const heroImageUrl = content?.hero_image?.download
    ? flattenToAppURL(content.hero_image.download)
    : null;

  // Card elements
  const internalRangeStart = internalItems.length
    ? (internalPage - 1) * internalItemsPerPage + 1
    : 0;
  const internalRangeEnd = internalRangeStart + internalItems.length - 1;

  const externalRangeStart = externalItems.length
    ? (externalPage - 1) * externalItemsPerPage + 1
    : 0;
  const externalRangeEnd = externalRangeStart + externalItems.length - 1;

  const showNextInternalCarouselPage = () => {
    setInternalCarouselStartIndex(0);
    setInternalPage((page) => page + 1);
  };

  const showPrevInternalCarouselPage = () => {
    setInternalCarouselStartIndex(internalItemsPerPage - 1);
    setInternalPage((page) => page - 1);
  };

  const showNextExternalCarouselPage = () => {
    setExternalCarouselStartIndex(0);
    setExternalPage((page) => page + 1);
  };

  const showPrevExternalCarouselPage = () => {
    setExternalCarouselStartIndex(externalItemsPerPage - 1);
    setExternalPage((page) => page - 1);
  };

  const internalCardElements = internalItems.map((item) => (
    <TeaserCardVertical
      key={item['@id']}
      title={item.title || ''}
      description={item.description || item.short_description || ''}
      imageUrl={resolveImageURL(item, '/@@images/thumbnail_image/preview')}
      url={
        item.media_news
          ? item.url || item.external_url || item['@id']
          : item['@id']
      }
      caption={formatNewsDate(item.effective || item.EffectiveDate)}
      tags={item.subjects || []}
    />
  ));

  const externalCardElements = externalItems.map((item) => (
    <TeaserCardVertical
      key={item['@id']}
      title={item.title || ''}
      description={item.description || item.short_description || ''}
      imageUrl={resolveImageURL(item, '/@@images/thumbnail_image/preview')}
      url={item.url || item.external_url || item['@id']}
      caption={formatNewsDate(item.effective || item.EffectiveDate)}
      tags={item.subjects || []}
    />
  ));

  return (
    <div className="simple-news-listing">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <PageHeaderBackground
        title={content?.title || 'News'}
        details=""
        description={
          content.short_description?.data || content.description?.data || ''
        }
        backgroundImage={heroImageUrl}
      />

      {/* ── Section 1: Internal news ─────────────────────────────────────── */}
      <PageSection
        paddingBottom="var(--ess-space-7xl)"
        ref={internalSectionRef}
      >
        <ContentWrapper
          className="simple-news-listing__section"
          minHeight="55vh"
        >
          <ContentWide alignment="center">
            {internalError ? (
              <p className="simple-news-listing__error" role="alert">
                {intl.formatMessage(messages.errorInternal)}
              </p>
            ) : internalLoading || !internalLoaded ? (
              <p className="simple-news-listing__loading" aria-live="polite">
                Loading…
              </p>
            ) : internalItems.length === 0 ? (
              <p className="simple-news-listing__empty">
                {intl.formatMessage(messages.noInternalNews)}
              </p>
            ) : (
              <ResponsiveComponentSwitch
                breakpoints={[
                  {
                    minWidth: 768,
                    component: (
                      <>
                        <ResponsiveGrid
                          breakpoints={INTERNAL_GRID_BREAKPOINTS}
                          className="simple-news-listing__grid"
                        >
                          {internalCardElements}
                        </ResponsiveGrid>
                        {internalTotalPages > 1 && (
                          <div className="simple-news-listing__pagination">
                            <Pagination
                              currentPage={internalPage}
                              totalPages={internalTotalPages}
                              onPageChange={(page) => {
                                setInternalPage(page);
                                scrollToRef(internalSectionRef);
                              }}
                            />
                          </div>
                        )}
                      </>
                    ),
                  },
                ]}
                defaultComponent={
                  <>
                    <Carousel
                      key={`${internalPage}-${internalCarouselStartIndex}`}
                      pageKey={`${internalPage}-${internalCarouselStartIndex}`}
                      startIndex={internalCarouselStartIndex}
                      onEdgeNext={
                        internalPage < internalTotalPages
                          ? showNextInternalCarouselPage
                          : undefined
                      }
                      onEdgePrev={
                        internalPage > 1
                          ? showPrevInternalCarouselPage
                          : undefined
                      }
                      range={{
                        start: internalRangeStart,
                        end: internalRangeEnd,
                        total: internalTotal,
                      }}
                    >
                      {internalCardElements}
                    </Carousel>
                  </>
                }              
              />
            )}
          </ContentWide>
        </ContentWrapper>
      </PageSection>

      {/* ── Section 2: External news ─────────────────────────────────────── */}
      <PageSection
        className="simple-news-listing__external-section"
        ref={externalSectionRef}
      >
        <ContentWrapper>
          <ContentWide alignment="center" mobileAlignment="center">
            <h2 className="simple-news-listing__section-title">
              {content?.external_news_title ||
                intl.formatMessage(messages.externalNewsTitle)}
            </h2>

            {externalError ? (
              <p className="simple-news-listing__error" role="alert">
                {intl.formatMessage(messages.errorExternal)}
              </p>
            ) : externalLoading || !externalLoaded ? (
              <p className="simple-news-listing__loading" aria-live="polite">
                Loading…
              </p>
            ) : externalItems.length === 0 ? (
              <p className="simple-news-listing__empty">
                {intl.formatMessage(messages.noExternalNews)}
              </p>
            ) : (
              <>
                <ResponsiveComponentSwitch
                  breakpoints={[
                    {
                      minWidth: 768,
                      component: (
                        <>
                          <ResponsiveGrid
                            breakpoints={EXTERNAL_GRID_BREAKPOINTS}
                            className="simple-news-listing__grid"
                          >
                            {externalCardElements}
                          </ResponsiveGrid>
                          {externalTotalPages > 1 && (
                            <div className="simple-news-listing__pagination">
                              <Pagination
                                currentPage={externalPage}
                                totalPages={externalTotalPages}
                                onPageChange={(page) => {
                                  setExternalPage(page);
                                  scrollToRef(externalSectionRef);
                                }}
                              />
                            </div>
                          )}
                        </>
                      ),
                    },
                  ]}
                  defaultComponent={
                    <>
                      <Carousel
                        key={`${externalPage}-${externalCarouselStartIndex}`}
                        pageKey={`${externalPage}-${externalCarouselStartIndex}`}
                        startIndex={externalCarouselStartIndex}
                        onEdgeNext={
                          externalPage < externalTotalPages
                            ? showNextExternalCarouselPage
                            : undefined
                        }
                        onEdgePrev={
                          externalPage > 1
                            ? showPrevExternalCarouselPage
                            : undefined
                        }
                        range={{
                          start: externalRangeStart,
                          end: externalRangeEnd,
                          total: externalTotal,
                        }}
                      >
                        {externalCardElements}
                      </Carousel>
                    </>
                  }   
                />
              </>
            )}
          </ContentWide>
        </ContentWrapper>
      </PageSection>
    </div>
  );
};

SimpleNewsListing.propTypes = {
  content: PropTypes.shape({
    '@id': PropTypes.string,
    '@type': PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    hero_image: PropTypes.object,
    external_news_title: PropTypes.string,
  }),
};

export default SimpleNewsListing;
